import React, { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { authPost, authPut } from "../../../lib";

const useStyles = makeStyles((theme) => ({
  fields: {
    display: "grid",
    gridTemplateColumns: "repeat(12, 1fr)",
    gap: theme.spacing(1.5),
    paddingTop: theme.spacing(0.5),
    width: 560,
    maxWidth: "100%",
  },
  flags: { gridColumn: "span 12", display: "flex", gap: theme.spacing(2) },
  error: { color: "#c62828", fontSize: 13, marginTop: theme.spacing(1) },
  actions: { padding: theme.spacing(1, 3, 2) },
  spacer: { flex: 1 },
}));

const FIELDS = [
  { name: "first_name", label: "First Name", col: 5 },
  { name: "mi", label: "MI", col: 2 },
  { name: "last_name", label: "Last Name", col: 5 },
  { name: "business_name", label: "Business Name", col: 12 },
  { name: "address1", label: "Address 1", col: 12 },
  { name: "address2", label: "Address 2", col: 12 },
  { name: "city", label: "City", col: 5 },
  { name: "state", label: "State", col: 3 },
  { name: "zip", label: "Zip", col: 2 },
  { name: "country", label: "Country", col: 2 },
  { name: "home_phone", label: "Home Phone", col: 6 },
  { name: "contact_phone", label: "Contact Phone", col: 6 },
];

/**
 * Add or edit one address on an account.
 *
 * Only changed fields are sent on an edit, so two people working on different
 * parts of the same account do not overwrite each other.
 */
export function AccountAddressDialog({ accountId, address, onClose, onSaved }) {
  const classes = useStyles();
  const editing = !!address?.address_id;

  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const seed = {};
    FIELDS.forEach((f) => {
      seed[f.name] = address?.[f.name] ?? "";
    });
    seed.country = seed.country || "US";
    seed.default_bill = !!Number(address?.default_bill);
    seed.default_ship = !!Number(address?.default_ship);
    setForm(seed);
    setError("");
  }, [address]);

  const set = (name) => (e) =>
    setForm((s) => ({ ...s, [name]: e.target.value }));

  const save = async () => {
    setBusy(true);
    setError("");

    let body;
    if (editing) {
      body = {};
      FIELDS.forEach((f) => {
        if (String(address?.[f.name] ?? "") !== String(form[f.name] ?? "")) {
          body[f.name] = form[f.name];
        }
      });
      ["default_bill", "default_ship"].forEach((f) => {
        if (!!Number(address?.[f]) !== !!form[f]) body[f] = form[f] ? 1 : 0;
      });
      if (!Object.keys(body).length) {
        setBusy(false);
        onClose();
        return;
      }
    } else {
      body = { ...form, default_bill: form.default_bill ? 1 : 0, default_ship: form.default_ship ? 1 : 0 };
    }

    const { error: err } = editing
      ? await authPut(`/accounts/${accountId}/addresses/${address.address_id}`, body)
      : await authPost(`/accounts/${accountId}/addresses`, body);
    setBusy(false);

    if (err) {
      setError(err.message || "Could not save that address.");
      return;
    }
    onSaved();
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="md">
      <DialogTitle>{editing ? "Edit Address" : "Add Address"}</DialogTitle>
      <DialogContent>
        <div className={classes.fields}>
          {FIELDS.map((f) => (
            <TextField
              key={f.name}
              fullWidth
              label={f.label}
              style={{ gridColumn: `span ${f.col}` }}
              value={form[f.name] ?? ""}
              onChange={set(f.name)}
            />
          ))}
          <div className={classes.flags}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={!!form.default_bill}
                  onChange={(e) => setForm((s) => ({ ...s, default_bill: e.target.checked }))}
                />
              }
              label="Default billing"
            />
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={!!form.default_ship}
                  onChange={(e) => setForm((s) => ({ ...s, default_ship: e.target.checked }))}
                />
              }
              label="Default shipping"
            />
          </div>
        </div>
        {error && <div className={classes.error}>{error}</div>}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button onClick={onClose} disabled={busy} size="small">
          Cancel
        </Button>
        <Button onClick={save} disabled={busy} size="small" variant="contained" color="primary">
          {busy ? "Saving…" : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AccountAddressDialog;
